import { refusalReasonWords } from '@/ui/words';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useAssignees, useOpenRequest } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentNames } from '@/app/departments';
import { Dialog } from '@/ui/Dialog';
import { Field, TextInput } from '@/ui/primitives';
import { Select } from '@/ui/fields';
import { OptionRow } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { useTechnical } from '@/app/technical';

/**
 * "You are not asking for access. You are asking them to do a task with
 * what they already hold." Two steps, and the routing answer comes back
 * with the request: with the agent, with the lead, or refused for want of
 * a grant only a person can make — which is a 200, and is shown as one.
 */
export function OpenRequest({ onClose, initialTitle = '', onOpened }: { onClose: () => void; initialTitle?: string | undefined; onOpened?: (id: string) => void }) {
  const tech = useTechnical();
  const directory = useDepartmentNames();
  const departmentLabel = directory.label;
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const open = useOpenRequest();
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [to, setTo] = useState<string | null>(null);
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState('');
  const [tools, setTools] = useState('');
  const [scopes, setScopes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Who there the person hopes will take it (ADR-0157): that department's
  // agents and its lead. A hint its router honours only for its own.
  const [prefer, setPrefer] = useState('');
  const there = useAssignees(me.department, to);

  // Whom a request can go to is every department the platform has, not a
  // list this screen remembers; a new one is askable the day it is synced.
  const departments = (directory.names ?? []).filter((d) => d !== me.department);
  const split = (text: string) => text.split(/[,\s]+/).map((n) => n.trim()).filter(Boolean);
  const needs = { tools: split(tools), scopes: split(scopes) };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={step === 1 ? 'Who, and what for? · Step 1 of 2' : 'Anything they should know? · Step 2 of 2'}
      title="Ask another department"
      secondary={{ label: step === 1 ? 'Cancel' : 'Back', onClick: () => (step === 1 ? onClose() : setStep(1)) }}
      primary={
        step === 1
          ? {
              label: 'Next',
              onClick: () => {
                if (!to) return setError('Pick a department.');
                if (title.trim() === '') return setError('Say what you need.');
                setError(null);
                setStep(2);
              },
            }
          : {
              label: 'Send the request',
              disabled: open.isPending,
              onClick: () =>
                open.mutate(
                  {
                    toDepartment: to!,
                    title: title.trim(),
                    ...(body.trim() ? { body: body.trim() } : {}),
                    ...(needs.tools.length || needs.scopes.length ? { needs } : {}),
                    ...(prefer.startsWith('a:') ? { preferAgent: prefer.slice(2) } : prefer.startsWith('h:') ? { preferHuman: prefer.slice(2) } : {}),
                  },
                  {
                    onSuccess: ({ request, routing }) => {
                      toast({
                        text:
                          routing.outcome === 'refused'
                            ? routing.reason.startsWith('needs_grant:')
                              ? `${departmentLabel(to!)} cannot do this with what it has. Your lead has a task naming whom to ask.`
                              : `${departmentLabel(to!)} cannot take it: ${refusalReasonWords(routing.reason)}.`
                            : routing.routedTo === 'agent'
                              ? `With ${departmentLabel(to!)}'s agent now.`
                              : `With ${departmentLabel(to!)}'s lead — it has no agent for this, so a person has it.`,
                        ...(routing.outcome === 'refused' ? { tone: 'danger' as const } : {}),
                      });
                      onClose();
                      if (onOpened) onOpened(request.id);
                      else void navigate(`/requests/${request.id}`);
                    },
                    onError: (e) => setError(said(failureOf(e))),
                  },
                ),
            }
      }
    >
      {step === 1 ? (
        <>
          <p className="m-0 text-body">
            You are not asking for access. You are asking them to do a task with what they already hold.
          </p>
          <Field label="Ask" error={error && !to ? error : undefined}>
            <OptionRow
              name="Department"
              value={to}
              onChoose={(d) => {
                setTo(d);
                setPrefer('');
              }}
              options={departments.map((d) => ({ value: d, label: departmentLabel(d) }))}
            />
          </Field>
          {to && (there.data?.agents.length || there.data?.people.length) ? (
            <Field label="Who there, if you know" hint="Their router gives it to whoever you name if they work there, and decides itself otherwise.">
              <Select
                aria-label="Who there"
                value={prefer}
                onChange={(e) => setPrefer(e.target.value)}
                options={[
                  { value: '', label: 'Whoever they choose' },
                  ...(there.data?.agents ?? []).map((a) => ({ value: `a:${a.id}`, label: `${a.givenName} Agent` })),
                  ...(there.data?.people ?? []).map((p) => ({ value: `h:${p.id}`, label: `${p.displayName}${p.isLead ? ' · leads there' : ''}` })),
                ]}
              />
            </Field>
          ) : null}
          <Field label="What you need" error={error && to ? error : undefined}>
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Last quarter's invoices, by customer" />
          </Field>
        </>
      ) : (
        <>
          <Field label="Detail" hint="What they read. Sealed to the two departments; nobody else.">
            <textarea
              aria-label="Detail"
              className="min-h-[120px] w-full rounded-brand border border-control bg-raised p-3 text-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Amounts and dates only — no line items."
            />
          </Field>
          {/* What a request needs by tool and scope id is the superadmin's to name. */}
          {tech && <>
          <Field
            label="Tools it takes"
            hint="Optional. Tool ids, separated by commas. The receiver is refused if it does not hold them — a request confers nothing."
          >
            <TextInput value={tools} onChange={(e) => setTools(e.target.value)} placeholder="voyager.linkedin_lookup" />
          </Field>
          <Field label="Scopes it takes" hint="Optional. Scopes, separated by commas.">
            <TextInput value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="finance.read" />
          </Field>
          </>}
          {error && <p className="m-0 text-dense text-danger">{error}</p>}
          {open.isError && <Refusal failure={failureOf(open.error)} compact />}
        </>
      )}
    </Dialog>
  );
}
